import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { authApi } from '../api/auth'
import { useAuthStore } from '../store/authStore'
import { EDGE_APPS, openApp, SCOPE_EDGE_COMPANIES, SCOPE_EDGE_USERS } from '../config/apps'
import AuthShell from '../components/AuthShell'
import { inputCls, labelCls, primaryBtn } from '../components/form'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const { setAuth } = useAuthStore()
  const navigate = useNavigate()

  // 접근 가능한 앱(토큰 aud)이 하나면 바로 진입, 둘 이상(또는 없음)이면 선택 화면.
  // 단 외부 앱(보드 등)만 쓰는 Admin/Manager 는 하나여도 선택 화면을 거친다 —
  // 조직 관리(EDGE 콘솔) 진입점이 런처에 있어서, 바로 내보내면 콘솔에 갈 방법이 없다.
  const enterDestination = () => {
    const { apps, role, can } = useAuthStore.getState()
    const available = EDGE_APPS.filter(a => apps.includes(a.id))
    const canManage = role === 'Manager' || can(SCOPE_EDGE_USERS) || can(SCOPE_EDGE_COMPANIES)
    if (available.length === 1 && (available[0].internal || !canManage)) openApp(available[0], navigate)
    else navigate('/apps')
  }

  // 세션이 살아 있는데 /login 으로 들어오면(북마크·진입 링크) 폼을 건너뛰고 바로 목적지로.
  // 만료된 세션이었다면 첫 API 401 → 리프레시 실패 → endSession 이 다시 /login 으로 보낸다.
  useEffect(() => {
    if (useAuthStore.getState().isAuthenticated()) enterDestination()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const { data } = await authApi.login(email, password)
      setAuth(data.accessToken, data.refreshToken, data.role, data.companyId)
      enterDestination()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      setError(msg || '로그인에 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthShell
      title="로그인"
      footer={<Link to="/forgot-password" className="text-gray-500 hover:text-gray-900 transition-colors">비밀번호를 잊으셨나요?</Link>}>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="login-id" className={labelCls}>아이디 또는 이메일</label>
          <input id="login-id" type="text" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)}
            required autoFocus className={inputCls} />
        </div>
        <div>
          <label htmlFor="login-pw" className={labelCls}>비밀번호</label>
          <input id="login-pw" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)}
            required className={inputCls} />
        </div>
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        <button type="submit" disabled={loading} className={`w-full ${primaryBtn} py-2.5`}>
          {loading ? '로그인하는 중…' : '로그인'}
        </button>
      </form>
    </AuthShell>
  )
}
